import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import type { TaskBodyProps } from '../index';
import { fetchPatientCard } from '../../../../services/patientCard';
import { advanceWorkflow } from '../../../../services/workQueue';
import { useToast } from '../../../../providers/ToastProvider';
import { apiErrorMessage } from '../../../../utils/apiError';
import {
    CARD_STALE_MS,
    REFINEMENT_STEPS,
    type FooterAction,
    type RegisterFooter,
    type RegisterBusy,
    type StepId
} from './spark.shared';
import { CreateRefinementStep } from './CreateRefinementStep';
import { UploadPhotosStep } from './UploadPhotosStep';
import { UploadScansStep } from './UploadScansStep';
import { RefinementStepper } from './RefinementStepper';
import { RefinementPhotosHeader } from './RefinementPhotosHeader';

/**
 * "Create refinement in manufacturer" — guided Spark wizard. Step 1 creates a
 * *refinement* case off the patient's parent case; steps 2–3 reuse
 * the primary submission wizard's Photos and Scans steps, targeting the new
 * refinement case (its id lives only in wizard state — it is never persisted).
 * Finishing fires the bare "create-refinement-in-manufacturer" transition.
 */
export function SparkRefinementWizard({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const { showToast } = useToast();

    // Passing the completed action to onTaskComplete is what makes the surface
    // show the completion card instead of silently closing back to the queue.
    const forwardAction = statusMeta.outgoingActions.find(
        (a) => a.action === 'create-refinement-in-manufacturer'
    );

    const { data: card } = useQuery({
        queryKey: ['patient-card', c.id],
        queryFn: () => fetchPatientCard(c.id),
        staleTime: CARD_STALE_MS,
        enabled: Boolean(c.id)
    });

    const [stepIndex, setStepIndex] = useState(0);
    // The NEW refinement case (created in step 1, held only here — not persisted).
    const [caseId, setCaseId] = useState<string | null>(null);
    const [link, setLink] = useState<string | null>(null);
    const [photosUploaded, setPhotosUploaded] = useState(false);
    const [scansUploaded, setScansUploaded] = useState(false);
    const [advancing, setAdvancing] = useState(false);
    const [stepBusy, setStepBusy] = useState(false);
    const [footer, setFooter] = useState<FooterAction | null>(null);
    const [photosProgress, setPhotosProgress] = useState<{ placed: number; total: number } | null>(
        null
    );
    // Photo/scan selections live here so they survive step navigation.
    const [photoAssignments, setPhotoAssignments] = useState<Record<string, string>>({});
    const photosPrefilledRef = useRef(false);
    const [uploadedPhotosSig, setUploadedPhotosSig] = useState<string | null>(null);
    const [scanSelected, setScanSelected] = useState<Record<string, boolean>>({});

    const registerFooter = useCallback<RegisterFooter>((action) => setFooter(action), []);
    const registerPhotosProgress = useCallback(
        (progress: { placed: number; total: number } | null) => setPhotosProgress(progress),
        []
    );
    const registerBusy = useCallback<RegisterBusy>((busy) => setStepBusy(busy), []);

    const locked = stepBusy || advancing;

    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true, busy: locked });
    }, [onPayloadChange, locked]);

    const currentStep = REFINEMENT_STEPS[stepIndex];
    // Upload steps target the new refinement case (id is a string in state).
    const refinementCaseId = caseId ? Number(caseId) : undefined;

    const stepDone = useMemo<Record<StepId, boolean>>(
        () => ({
            case: false,
            refinement: Boolean(caseId),
            photos: photosUploaded,
            scans: scansUploaded
        }),
        [caseId, photosUploaded, scansUploaded]
    );

    const goBack = useCallback(() => setStepIndex((i) => Math.max(i - 1, 0)), []);

    // Step 1 → store the refinement refs and advance to photos.
    const handleRefinementReady = useCallback((id: string, url: string) => {
        setCaseId(id);
        setLink(url);
        setStepIndex(1);
    }, []);

    const handlePhotosContinue = useCallback((uploadedSig: string) => {
        setPhotosUploaded(true);
        setUploadedPhotosSig(uploadedSig);
        setStepIndex(2);
    }, []);

    // Returns true once the case has advanced, so the scans step can hold its
    // success toast until right before the redirect.
    const handleFinish = useCallback(async (): Promise<boolean> => {
        if (!caseId) return false;
        setAdvancing(true);
        try {
            await advanceWorkflow(c.id, 'create-refinement-in-manufacturer');
            setScansUploaded(true);
            onTaskComplete?.(forwardAction);
            return true;
        } catch (err) {
            showToast(
                apiErrorMessage(err, 'Failed to advance the case. Please try again.'),
                'error'
            );
            return false;
        } finally {
            setAdvancing(false);
        }
    }, [c.id, caseId, forwardAction, onTaskComplete, showToast]);

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            <RefinementStepper
                stepIndex={stepIndex}
                stepDone={stepDone}
                locked={locked}
                onSelect={setStepIndex}
            />

            {currentStep.id === 'photos' && <RefinementPhotosHeader progress={photosProgress} />}

            {/*  Body  */}
            <div className="flex-1 min-h-0 overflow-auto p-6">
                {currentStep.id === 'refinement' && (
                    <CreateRefinementStep
                        patientId={c.id}
                        card={card}
                        caseId={caseId}
                        link={link}
                        onReady={handleRefinementReady}
                        registerFooter={registerFooter}
                    />
                )}
                {currentStep.id === 'photos' && (
                    <UploadPhotosStep
                        patientId={c.id}
                        caseId={refinementCaseId}
                        minPhotos={0}
                        onContinue={handlePhotosContinue}
                        registerFooter={registerFooter}
                        registerBusy={registerBusy}
                        registerPhotosProgress={registerPhotosProgress}
                        assignments={photoAssignments}
                        setAssignments={setPhotoAssignments}
                        prefilledRef={photosPrefilledRef}
                        uploadedSig={uploadedPhotosSig}
                    />
                )}
                {currentStep.id === 'scans' && (
                    <UploadScansStep
                        patientId={c.id}
                        caseId={refinementCaseId}
                        advancing={advancing}
                        onFinish={handleFinish}
                        registerFooter={registerFooter}
                        registerBusy={registerBusy}
                        selected={scanSelected}
                        setSelected={setScanSelected}
                    />
                )}
            </div>

            {/*  Footer - Back (bottom-left) + the active step's primary action (bottom-right).  */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 flex-shrink-0">
                <button
                    type="button"
                    onClick={goBack}
                    disabled={stepIndex === 0 || locked}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Back
                </button>
                {footer ? (
                    <button
                        type="button"
                        onClick={footer.onClick}
                        disabled={footer.disabled}
                        className="flex items-center gap-2 bg-gray-900 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {footer.label}
                    </button>
                ) : (
                    <span />
                )}
            </div>
        </div>
    );
}
